// --------------------------------------------------------------
// Created On: 2026-10-25
// Author: Zachary Thomas
//
// Last Modified: 2026-11-03
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState } from "react";
import { Link } from "react-router-dom";
import useRemoteOperation from "src/hooks/useRemoteOperation";
import userHasPermission from "src/utilities/userHasPermission";
import {
  DEVICES_THAT_SUPPORT_REMOTE_OPERATIONS,
  DEVICES_THAT_REPORT_READY_FOR_COMMANDS,
} from "src/constants/miscellaneous";
import { LOCK_REMOTE_CONTROL_PERMISSION } from "src/constants/permissions";
import RemoteOperationPanel from "src/components/RemoteOperationPanel/RemoteOperationPanel";
import ConfirmModal from "src/components/ConfirmModal/ConfirmModal";
import InfoBanner from "src/components/InfoBanner/InfoBanner";
import Success from "src/components/Success/Success";
import Warning from "src/components/Warning/Warning";
import Error from "src/components/Error/Error";
import Spinner from "src/components/Spinner/Spinner";
import Card from "src/components/Card/Card";
import LightIcon from "src/components/LightIcon/LightIcon";
import styles from "./RemoteOperationModalContent.module.scss";

// Remote operation controls for a single asset, with the same logic as the asset remote operation page.
export default function RemoteOperationModalContent(props: Props): Component {
  const [showConfirmLock, setShowConfirmLock] = useState<boolean>(false);
  const [showConfirmUnlock, setShowConfirmUnlock] = useState<boolean>(false);
  const {
    assetData,
    isLockedOut,
    attributeReadings,
    deviceIsReadyForCommand,
    messageMap,
    loading,
    failedToLoad,
    userCanOperate,
    isSamplingLiveData,
    actions,
  } = useRemoteOperation(props.assetId);
  const canLock = userHasPermission([[LOCK_REMOTE_CONTROL_PERMISSION]]);
  const supportsRemoteOperation =
    assetData !== null && DEVICES_THAT_SUPPORT_REMOTE_OPERATIONS.includes(assetData.deviceType || "");
  // A supported device still has nothing to operate unless it has a controller or digital outputs.
  const hasRemoteControls =
    supportsRemoteOperation && (assetData.controllerModel !== null || attributeReadings.digitalOutputs.length > 0);
  const deviceReportsReadiness =
    assetData !== null && DEVICES_THAT_REPORT_READY_FOR_COMMANDS.includes(assetData.deviceType || "");
  const showDeviceReadiness = deviceReportsReadiness && deviceIsReadyForCommand !== null;
  // The readiness light is only hidden once we know the device cannot report readiness, such as a SCADA RTU.
  const showReadinessLight = assetData === null || deviceReportsReadiness;
  const isCollectingLiveData = assetData !== null && isSamplingLiveData;

  // Returns the text of the device readiness light, which is blank until the readiness is known.
  function getReadyText(): string {
    if (!showDeviceReadiness) {
      return "Getting Device Status";
    } else if (deviceIsReadyForCommand) {
      return "Ready for Commands";
    } else {
      return "Not Ready for Commands";
    }
  }

  // Returns the color class of the device readiness light.
  function getReadyClassName(): string {
    if (!showDeviceReadiness) {
      return styles.neutral;
    } else if (deviceIsReadyForCommand) {
      return styles.ready;
    } else {
      return styles.notReady;
    }
  }

  // Returns the color class of the live data light.
  function getLiveClassName(): string {
    if (isCollectingLiveData) {
      return styles.ready;
    } else {
      return styles.neutral;
    }
  }

  // Ask the user to confirm before changing the lock status.
  function handleLockStatus(isLocked: boolean): void {
    if (isLocked) {
      setShowConfirmLock(true);
    } else {
      setShowConfirmUnlock(true);
    }
  }

  // Close the confirm dialogs and apply the lock change.
  function confirmLockStatus(isLocked: boolean): void {
    setShowConfirmLock(false);
    setShowConfirmUnlock(false);
    void actions.updateLockStatus(isLocked);
  }

  return (
    <div data-test="remote-operation-modal-content">
      {messageMap.error.length === 0 &&
        !failedToLoad &&
        !userCanOperate &&
        !loading &&
        assetData !== null &&
        !assetData.isMigrating &&
        !assetData.operationIsAllowed && (
          <div className={styles.message}>
            <InfoBanner message="You do not have permission to remotely operate this asset." />
          </div>
        )}

      {messageMap.error.length > 0 && (
        <div className={styles.message}>
          <Error message={messageMap.error} />
        </div>
      )}

      {failedToLoad && (
        <div className={styles.message}>
          <Error message="Unable to load remote operation data for this asset. Please try again." />
        </div>
      )}

      {/* The lock controls are hidden in this modal, so this note applies whether or not the user can lock assets. */}
      {!failedToLoad && assetData !== null && !loading && (assetData.deviceId === null || !hasRemoteControls) && (
        <div data-test="remote-operation-modal-not-supported" className={styles.message}>
          <Warning message="This asset does not support remote control operation due to its monitoring device / controller configuration." />
        </div>
      )}

      {messageMap.warning.length > 0 && (
        <div className={styles.message}>
          <Warning message={messageMap.warning} />
        </div>
      )}

      {messageMap.success.length > 0 && (
        <div className={styles.message}>
          <Success message={messageMap.success} />
        </div>
      )}

      {!failedToLoad && (
        <div data-test="remote-operation-modal-status" className={styles.statusBox}>
          {showReadinessLight && (
            <span data-test="remote-operation-modal-device-ready" className={`${styles.status} ${getReadyClassName()}`}>
              <LightIcon isOn={showDeviceReadiness} color={deviceIsReadyForCommand ? "green" : "red"} />
              {getReadyText()}
            </span>
          )}
          <span data-test="remote-operation-modal-live-data" className={`${styles.status} ${getLiveClassName()}`}>
            <LightIcon isOn={isCollectingLiveData} color="green" />
            {isCollectingLiveData ? "Sampling Live Data" : "Waiting for Commands"}
          </span>
        </div>
      )}

      {/* We show remote operation controls only if the asset is not locked out and has a valid device. */}
      {assetData !== null && !isLockedOut && assetData.operationIsAllowed && hasRemoteControls && (
        <RemoteOperationPanel
          assetName={assetData.assetName}
          deviceId={assetData.deviceId}
          deviceType={assetData.deviceType}
          isDeviceAttached={assetData.isDeviceAttached}
          isMigrating={assetData.isMigrating}
          remoteOperationSupported={assetData.remoteOperationSupported}
          controllerApplicationType={assetData.controllerApplicationType}
          controllerModel={assetData.controllerModel}
          isLockedOut={isLockedOut}
          canUnlock={assetData.canUnlock}
          loading={loading}
          firmwareUpdateInitialized={false}
          onLatestFirmware={assetData.onLatestFirmware}
          digitalOutputs={attributeReadings.digitalOutputs}
          hideMonitoringDeviceControls
          hideRemoteLockControls
          onChangeCommunicationMode={(command) => actions.changeCommunicationMode(command)}
          onChangeSpeed={(command) => actions.changeSpeed(command)}
          onChangeRunningStatus={(command) => actions.changeRunningStatus(command)}
          onClearFailedCommands={() => actions.clearFailedCommands()}
          onRestartMonitoringDevice={() => actions.restartMonitoringDevice()}
          onReapplyConfiguration={() => actions.reapplyConfiguration()}
          onBlinkLeds={(pattern, position) => actions.blinkLeds(pattern, position)}
          onError={(error) => actions.setErrorMessage(error)}
          onChangeLock={(isLocked) => handleLockStatus(isLocked)}
          onChangeValvePosition={(valveNumber, percentOpen) => actions.changeValvePosition(valveNumber, percentOpen)}
          onChangeDigitalOutput={(position, state) => actions.changeDigitalOutput(position, state)}
        />
      )}

      {/* We show this message if remote operation is locked for this asset. */}
      {assetData !== null && !loading && isLockedOut && (
        <Card title={`${assetData.assetName} Remote Operations`}>
          <p className="h4 text-center py-5 px-4">
            Remote control access is locked for this asset.
            {assetData.canUnlock
              ? ""
              : " The lock was applied by the company you are renting this asset from, only they can unlock this asset."}
          </p>
          {assetData.canUnlock && canLock && (
            <div className="text-center">
              <button
                className="btn btn-primary mx-auto mb-5"
                type="button"
                disabled={assetData.isMigrating}
                onClick={() => handleLockStatus(false)}
              >
                Unlock Remote Control
              </button>
            </div>
          )}
        </Card>
      )}

      <div data-test="remote-operation-modal-page-link" className={styles.pageLink}>
        <span className={styles.label}>Asset remote operation page</span>
        <Link
          data-test="remote-operation-modal-page-link-button"
          to={`/asset/${props.assetId}/operation`}
          className="btn btn-primary btn-sm text-nowrap"
        >
          Redirect to Page
        </Link>
      </div>

      <ConfirmModal
        showModal={showConfirmLock}
        title="Apply Remote Lock?"
        content={
          "Are you sure that you want to apply a remote lock on this asset? " +
          "This will prevent all remote operations for this asset until the lock is removed."
        }
        yesText="Apply Remote Lock"
        noText="Cancel"
        danger={false}
        onClose={() => setShowConfirmLock(false)}
        onYes={() => confirmLockStatus(true)}
        onNo={() => setShowConfirmLock(false)}
      />

      <ConfirmModal
        showModal={showConfirmUnlock}
        title="Remove Remote Lock?"
        content="Are you sure that you want to remove the remote lock on this asset?"
        yesText="Remove Remote Lock"
        noText="Cancel"
        danger={true}
        onClose={() => setShowConfirmUnlock(false)}
        onYes={() => confirmLockStatus(false)}
        onNo={() => setShowConfirmUnlock(false)}
      />

      <Spinner loading={loading} />
    </div>
  );
}

interface Props {
  assetId: number;
}
